import React, { useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Building2, ShoppingBag, Bot, Stethoscope, ArrowRight } from "lucide-react";

const productItems = [
  {
    label: "Workspace Management",
    to: "/products/workspace",
    tagline: "Book & manage workspaces",
    icon: Building2,
    color: "#ca262c",
  },
  {
    label: "BuildX",
    to: "/products/buildex",
    tagline: "Enterprise ecommerce platform",
    icon: ShoppingBag,
    color: "#ca262c",
  },
  {
    label: "ChatBot",
    to: "/products/chatbot",
    tagline: "AI-powered customer support",
    icon: Bot,
    color: "#ca262c",
  },
  {
    label: "Appointment",
    to: "/products/appointment",
    tagline: "AI doctor appointment assistant",
    icon: Stethoscope,
    color: "#ca262c",
  },
];

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Our Work", to: "/our-work" },
  { label: "Services", to: "/services" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeTimer = useRef(null);

  const openDropdown = () => {
    clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 120);
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">

        {/* ── Desktop nav ── */}
        <div className="hidden md:flex items-center space-x-8">
          <Link to="/" className="flex items-center">
            <img src="/images/logo.png" alt="SR Next" className="h-8 w-auto object-contain" />
          </Link>

          {navItems.map(({ label, to }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors hover:text-red-600 ${
                  isActive ? "text-red-600" : "text-gray-800"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdown}
          >
            <NavLink
              to="/products"
              className={({ isActive }) =>
                `flex items-center gap-1 text-sm font-semibold transition-colors hover:text-red-600 ${
                  isActive ? "text-red-600" : "text-gray-800"
                }`
              }
            >
              Products
              <ChevronDown
                className="w-3.5 h-3.5 transition-transform duration-200"
                style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </NavLink>
            {dropdownOpen && (
              <div
                style={{ width: 480, position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 20, zIndex: 9999 }}
              >
                {/* Caret */}
                <div
                  style={{
                    position: "absolute",
                    top: -6,
                    left: "50%",
                    transform: "translateX(-50%) rotate(45deg)",
                    width: 12,
                    height: 12,
                    background: "white",
                    borderLeft: "1px solid #e5e7eb",
                    borderTop: "1px solid #e5e7eb",
                  }}
                />

                {/* Panel */}
                <div
                  style={{
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    overflow: "hidden",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                  }}
                >
                  {/* Header row */}
                  <div style={{ padding: "14px 20px 10px", borderBottom: "1px solid #f3f4f6" }}>
                    <p style={{ color: "#ca262c", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", margin: 0 }}>
                      Our Products
                    </p>
                  </div>

                  {/* 2-column product grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 12 }}>
                    {productItems.map(({ label, to, tagline, icon: Icon, color }) => (
                      <Link
                        key={to}
                        to={to}
                        style={{ textDecoration: "none" }}
                        onClick={() => setDropdownOpen(false)}
                        onMouseEnter={e => (e.currentTarget.style.background = "#fef2f2")}
                        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                        className="flex items-start gap-3 p-3 rounded-xl transition-colors"
                      >
                        {/* Icon tile */}
                        <span
                          style={{
                            flexShrink: 0, width: 40, height: 40, borderRadius: 12,
                            background: "#fef2f2", border: "1px solid #fecaca",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <Icon style={{ width: 20, height: 20, color }} />
                        </span>

                        {/* Text */}
                        <span style={{ paddingTop: 2 }}>
                          <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.3, marginBottom: 3 }}>
                            {label}
                          </span>
                          <span style={{ display: "block", fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                            {tagline}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>

                  {/* Footer CTA bar */}
                  <div
                    style={{
                      margin: "0 12px 12px", padding: "10px 14px", borderRadius: 12,
                      background: "#f9fafb", border: "1px solid #e5e7eb",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                  >
                    <Link
                      to="/products"
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#374151" }}
                      onClick={() => setDropdownOpen(false)}
                      onMouseEnter={e => (e.currentTarget.style.color = "#ca262c")}
                      onMouseLeave={e => (e.currentTarget.style.color = "#374151")}
                    >
                      View all products
                      <ArrowRight style={{ width: 14, height: 14 }} />
                    </Link>
                    <Link
                      to="/contact-us"
                      style={{
                        textDecoration: "none", fontSize: 12, fontWeight: 600,
                        color: "white", background: "#ca262c",
                        padding: "6px 14px", borderRadius: 8,
                      }}
                      onClick={() => setDropdownOpen(false)}
                      onMouseEnter={e => (e.currentTarget.style.background = "#a01f24")}
                      onMouseLeave={e => (e.currentTarget.style.background = "#ca262c")}
                    >
                      Book a Demo
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="hidden md:block">
          <Link to="/contact-us" className="btn btn-orange">
            Contact us
          </Link>
        </div>

        <button
          className="md:hidden text-gray-700"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ── Mobile menu ── */}
      {mobileOpen && (
        <div className="md:hidden px-5 pb-5 space-y-1 border-t border-gray-100">
          {navItems.map(({ label, to }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block text-sm font-medium py-2.5 border-b border-gray-50 ${
                  isActive ? "text-red-600" : "text-gray-800"
                } hover:text-red-600`
              }
            >
              {label}
            </NavLink>
          ))}

          {/* Mobile Products accordion */}
          <div className="border-b border-gray-50">
            <div className="flex items-center justify-between">
              <NavLink
                to="/products"
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block text-sm font-medium py-2.5 ${
                    isActive ? "text-red-600" : "text-gray-800"
                  } hover:text-red-600`
                }
              >
                Products
              </NavLink>
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            {productsOpen && (
              <div className="pb-3 space-y-1">
                {productItems.map(({ label, to, tagline, icon: Icon, color }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => { setMobileOpen(false); setProductsOpen(false); }}
                    className="flex items-center gap-3 py-2.5 px-2 rounded-xl hover:bg-gray-50 transition-colors"
                    style={{ textDecoration: "none" }}
                  >
                    <span
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${color}18` }}
                    >
                      <Icon className="w-4 h-4" style={{ color }} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">{label}</span>
                      <span className="block text-xs text-gray-400">{tagline}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2">
            <Link
              to="/contact-us"
              className="block w-full text-center bg-red-600 text-white text-sm font-semibold py-3 rounded-xl hover:bg-red-700 transition-colors"
              style={{ textDecoration: "none" }}
              onClick={() => setMobileOpen(false)}
            >
              Contact us
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
